/* @flow */
import debounce from 'lodash.debounce';
import settings, { getSuggestedCity } from './settings';
import camelize from 'camelize';
import Raven from 'raven-js';

const MAPS_API_DEBOUNCE = 100;
const CEP_API_URL = __DEV__
  ? 'https://staging.loggi.com/api/v1/cep/'
  : '/api/v1/cep/';
const CEP_REGEX = /^\d{5}-?\d{3}$/;

export function embedGoogleMaps(window) {
  if (window.google) {
    return;
  }
  const document = window.document;
  const script = document.createElement('script');
  script.src = `//maps.google.com/maps/api/js?key=${
    settings.google_maps_api_key
  }&libraries=places&language=pt-br`;
  script.type = 'text/javascript';

  document.body.appendChild(script);
}

/**
 * Returns true in case Google API has not been loaded yet.
 * Callbacks will only be executed if the Google API has been loaded.
 */
export function waitGoogleMaps(callback) {
  const $window = window || {};

  if (!$window.googleMapsCallbacks) {
    $window.googleMapsCallbacks = [callback];
  } else {
    $window.googleMapsCallbacks.push(callback);
  }

  if (!$window.googleCheckInterval) {
    $window.googleCheckInterval = setInterval(() => {
      if ($window.google) {
        clearInterval($window.googleCheckInterval);
        $window.googleMapsCallbacks.forEach(fn => {
          fn($window.google);
        });
        $window.googleMapsCallbacks = null;
      }
    }, 10);

    return true;
  }

  if ($window.google) {
    return false;
  } else {
    return true;
  }
}

export function loadGoogleMaps(callback) {
  const $window = window || {};

  if (!$window.google) {
    embedGoogleMaps($window);
    waitGoogleMaps(callback);
    return;
  }
  callback($window.google);
}

export function generateAutocompleteSessionToken() {
  const { google } = window;
  return new google.maps.places.AutocompleteSessionToken();
}

function getCityBounds(google) {
  const suggestedCity = getSuggestedCity(settings);
  const hasSuggestedCity =
    suggestedCity !== null && suggestedCity !== undefined;
  if (!hasSuggestedCity) {
    return null;
  }

  const [sw, ne] = suggestedCity.geo.bound_limit;
  const edges = {
    sw: new google.maps.LatLng(sw.lat, sw.lng),
    ne: new google.maps.LatLng(ne.lat, ne.lng)
  };

  return new google.maps.LatLngBounds(edges.sw, edges.ne);
}

function getBoundsFromList(google, bounds) {
  if (bounds === null) {
    return null;
  }

  const [swLat, swLng, neLat, neLng] = bounds;
  return new google.maps.LatLngBounds(
    new google.maps.LatLng(swLat, swLng),
    new google.maps.LatLng(neLat, neLng)
  );
}

function getBounds(google, bounds = null) {
  if (bounds === null) {
    return getCityBounds(google);
  } else {
    return getBoundsFromList(google, bounds);
  }
}

function getComponentsRestrictions() {
  return { country: 'BR' };
}

function mapsGeocode(google, input, bounds, callback) {
  const geocoder = new google.maps.Geocoder();
  const restrictions = getComponentsRestrictions();

  let payload = {
    componentRestrictions: restrictions,
    bounds: bounds
  };

  if (typeof input === 'string') {
    payload.address = input;
  } else {
    payload = Object.assign(payload, input);
  }

  if (payload.placeId) {
    delete payload.componentRestrictions;
  }

  geocoder.geocode(payload, (results, status) => {
    if (status !== google.maps.GeocoderStatus.OK) {
      callback([]);
      return;
    }

    callback(camelize(results));
  });
}

function addressDataToSuggestion(addressData, filterInfo = null) {
  const route = addressData.addressComponents.find(
    ({ types }) => types[0] === 'route'
  );
  const streetNumber = addressData.addressComponents.find(
    ({ types }) => types[0] === 'street_number'
  );

  let info = addressData.addressComponents.filter(
    filterInfo
      ? filterInfo
      : ({ types }) =>
          types.indexOf('political') > -1 &&
          types.indexOf('country') === -1 &&
          types.indexOf('administrative_area_level_2') === -1
  );

  return {
    placeId: addressData.placeId || null,
    description: addressData.formattedAddress,
    structuredFormatting: {
      mainText: streetNumber
        ? `${route.shortName}, ${streetNumber.shortName}`
        : route.shortName,
      secondaryText: info.map(i => i.shortName).join(', ')
    }
  };
}

function mapsPredictions(
  google,
  input,
  bounds,
  callback,
  isPlacesEnabled = false,
  sessionToken = null
) {
  const autocomplete = new google.maps.places.AutocompleteService();
  const restrictions = getComponentsRestrictions();
  const enableGeocodeFallback = typeof input === 'string';
  const googleMapsTypes = isPlacesEnabled ? [] : ['address'];

  const payload = {
    input: input,
    bounds: bounds,
    types: googleMapsTypes,
    componentRestrictions: restrictions
  };

  if (!enableGeocodeFallback) {
    payload.input = input.query;
    payload.types = input.types;
  }

  if (sessionToken) {
    payload.sessionToken = sessionToken;
  }

  Raven.captureBreadcrumb({
    message: 'Autocomplete session token',
    category: 'google-apis',
    data: {
      sessionToken: payload.sessionToken
    }
  });

  autocomplete.getPlacePredictions(payload, (predictions, status) => {
    if (status !== google.maps.places.PlacesServiceStatus.OK) {
      if (status === 'ZERO_RESULTS' && enableGeocodeFallback) {
        if (input.match(CEP_REGEX)) {
          fetch(CEP_API_URL, {
            method: 'POST',
            mode: 'cors',
            body: JSON.stringify({ cep: input })
          })
            .then(response => response.json())
            .then(data => {
              const addressData = camelize(data.address_data);
              addressData.addressComponents = addressData.addressComponents
                .filter(({ types }) => types.indexOf('street_number') === -1)
                .map(component => {
                  if (component.types.indexOf('postal_code') > -1) {
                    component.shortName = component.shortName.replace('-', '');
                  }
                  return component;
                });
              addressData.addressComponents.push({
                types: [],
                shortName: 'Brazil'
              });
              callback([
                addressDataToSuggestion(
                  addressData,
                  ({ types }) =>
                    types.indexOf('route') === -1 &&
                    types.indexOf('street_number') === -1 &&
                    types.indexOf('administrative_area_level_2') === -1 &&
                    types.indexOf('country') === -1
                )
              ]);
            });
          return;
        }

        const cep = input.match(/\d{5}-?\d{3}/);
        const geocodeInput = input.replace(/\, /gi, ' ').replace(/\-/gi, ' ');
        mapsGeocode(google, geocodeInput, bounds, results => {
          let geocodes = results.filter(r =>
            r.addressComponents.find(a => a.types.indexOf('route') > -1)
          );

          if (cep) {
            geocodes = geocodes.filter(r => {
              const postalCode = r.addressComponents.find(
                a => a.types.indexOf('postal_code') > -1
              );
              return (
                postalCode &&
                postalCode.shortName.startsWith(cep[0].slice(0, 5))
              );
            });
          }
          callback(
            geocodes.map(r => {
              return addressDataToSuggestion(r);
            })
          );
        });
        return;
      }
      callback([]);
      return;
    }
    callback(camelize(predictions));
  });
}

function placeDetail(google, placeId, sessionToken, callback) {
  const service = new google.maps.places.PlacesService(
    document.createElement('div')
  );
  const request = {
    placeId: placeId,
    sessionToken: sessionToken,
    fields: [
      'address_components',
      'formatted_address',
      'geometry',
      'place_id',
      'types',
      'vicinity'
    ]
  };
  Raven.captureBreadcrumb({
    message: 'Place details session token',
    category: 'google-apis',
    data: {
      sessionToken: sessionToken
    }
  });
  service.getDetails(request, (place, status) => {
    if (status === google.maps.places.PlacesServiceStatus.OK) {
      callback(place);
    }
  });
}

export function addressGeocode(input, callback, bounds = null) {
  if (!input) {
    callback([]);
    return;
  }
  loadGoogleMaps(google => {
    mapsGeocode(google, input, getBounds(google, bounds), results => {
      callback(results);
    });
  });
}

const predictions = debounce(mapsPredictions, MAPS_API_DEBOUNCE);
export function addressAutocomplete(
  input,
  callback,
  bounds = null,
  isPlacesEnabled = false,
  sessionToken = null
) {
  if (!input) {
    callback([]);
    return;
  }
  loadGoogleMaps(google => {
    predictions(
      google,
      input,
      getBounds(google, bounds),
      results => {
        callback(results);
      },
      isPlacesEnabled,
      sessionToken
    );
  });
}

export function addressPlaceDetail(placeId, sessionToken, callback) {
  loadGoogleMaps(google => {
    placeDetail(google, placeId, sessionToken, callback);
  });
}
